/**
 * Safe localStorage wrapper for iOS Safari Private Browsing Mode
 * 
 * iOS Safari throws SecurityError when accessing localStorage in private mode
 * This wrapper provides safe fallback behavior using in-memory storage
 */

class SafeStorage {
  private isAvailable: boolean;
  private memoryFallback: Map<string, string>;

  constructor() {
    this.memoryFallback = new Map();
    this.isAvailable = this.checkAvailability();
  }

  private checkAvailability(): boolean {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      console.warn('localStorage not available (likely iOS Private Mode), using memory fallback');
      return false;
    }
  }

  getItem(key: string): string | null {
    if (this.isAvailable) {
      try {
        return localStorage.getItem(key);
      } catch (e) {
        console.warn(`localStorage.getItem failed for key: ${key}`, e);
        return this.memoryFallback.get(key) || null;
      }
    }
    return this.memoryFallback.get(key) || null;
  }

  setItem(key: string, value: string): void {
    if (this.isAvailable) {
      try {
        localStorage.setItem(key, value);
        return;
      } catch (e) {
        console.warn(`localStorage.setItem failed for key: ${key}`, e);
      }
    }
    // Fallback to memory storage
    this.memoryFallback.set(key, value);
  }

  removeItem(key: string): void {
    if (this.isAvailable) {
      try {
        localStorage.removeItem(key);
      } catch (e) {
        console.warn(`localStorage.removeItem failed for key: ${key}`, e);
      }
    }
    this.memoryFallback.delete(key);
  }

  clear(): void {
    if (this.isAvailable) {
      try {
        localStorage.clear();
      } catch (e) {
        console.warn('localStorage.clear failed', e);
      }
    }
    this.memoryFallback.clear();
  }
}

export const safeStorage = new SafeStorage();
export default safeStorage;

